import { useMemo } from 'react';
import useIsTenantAdmin from './useIsTenantAdmin';
import { LockMetadata } from '../types/validationLockTypes';

export const formatLockTimestamp = (val: string | number | undefined): string => {
  if (!val) return '';
  try {
    const trimmed = typeof val === 'string' ? val.trim() : '';
    const isPureDigits = /^\d+$/.test(trimmed);
    if (typeof val === 'number' || isPureDigits) {
      let num = typeof val === 'number' ? val : Number(trimmed);
      if (num > 0 && num < 10000000000) {
        num = num * 1000;
      }
      const d = new Date(num);
      return !isNaN(d.getTime()) ? d.toISOString() : (trimmed || String(val));
    }
    return trimmed || String(val);
  } catch {
    return String(val);
  }
};

export interface ItemLockState {
  isLocked: boolean;
  isReadOnly: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canUnlock: boolean;
  lockTooltip: string;
  isCascadeLocked: boolean;
  cascadePlanName?: string;
  lockedBy?: string;
  lockedAtFormatted?: string;
}

/**
 * Reusable domain hook to evaluate lock status for any validation entity.
 * Provides ready-to-use flags for disabling actions and rendering tooltips.
 */
export const useItemLock = (item: LockMetadata | null | undefined): ItemLockState => {
  const isTenantAdmin = useIsTenantAdmin();

  return useMemo(() => {
    const isLocked = Boolean(item?.locked);
    const lockedBy = item?.lastLockUpdateBy || item?.lockedBy;
    const lockedAtFormatted = formatLockTimestamp(item?.lastLockUpdateAt ?? item?.lockedAt);

    const referencingPlans = item?.referencingLockedPlans;
    const isCascadeLocked = Boolean(referencingPlans && referencingPlans.length > 0);
    const cascadePlanName = referencingPlans?.[0]?.name;
    const planNames = referencingPlans?.map(p => `'${p.name}'`).join(', ');

    // Direct unlock is blocked if locked by a plan cascade (backend returns 409)
    const canUnlock = isTenantAdmin && !isCascadeLocked;

    let lockTooltip = '';
    if (isLocked) {
      if (isCascadeLocked && planNames) {
        lockTooltip = (referencingPlans?.length ?? 0) > 1
          ? `Locked by Test Plans ${planNames}. Unlock the plans first.`
          : `Locked by Test Plan ${planNames}. Unlock the plan first.`;
      } else if (lockedBy) {
        lockTooltip = `Locked by ${lockedBy}${lockedAtFormatted ? ` on ${lockedAtFormatted}` : ''}`;
      } else if (lockedAtFormatted) {
        lockTooltip = `Locked on ${lockedAtFormatted}`;
      } else {
        lockTooltip = 'Item is locked';
      }
    }

    return {
      isLocked,
      isReadOnly: isLocked,
      canEdit: !isLocked,
      canDelete: !isLocked,
      canUnlock,
      lockTooltip,
      isCascadeLocked,
      cascadePlanName,
      lockedBy,
      lockedAtFormatted,
    };
  }, [item?.locked, item?.lastLockUpdateBy, item?.lockedBy, item?.lastLockUpdateAt, item?.lockedAt, item?.referencingLockedPlans, isTenantAdmin]);
};

export default useItemLock;
